import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Package as PackageType } from '../../services/packages.service'
import {
  Edit,
  Trash2,
  Copy,
  Eye,
  Star,
  MapPin,
  DollarSign,
  Package as PackageIcon,
  MoreHorizontal,
  ChevronDown,
  ChevronUp
} from 'lucide-react'

interface PackageListProps {
  packages: PackageType[]
  onEdit: (pkg: PackageType) => void
  onDelete: (pkg: PackageType) => void
  onClone: (pkg: PackageType) => void
}

const packageTypeColors = {
  BASIC: 'bg-gray-100 text-gray-800 border-gray-200',
  STANDARD: 'bg-blue-100 text-blue-800 border-blue-200',
  PREMIUM: 'bg-green-100 text-green-800 border-green-200',
  CUSTOM: 'bg-purple-100 text-purple-800 border-purple-200'
}

const packageTypeOrder = { BASIC: 1, STANDARD: 2, PREMIUM: 3, CUSTOM: 4 }

export default function PackageList({ packages, onEdit, onDelete, onClone }: PackageListProps) {
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set())
  const [showMenu, setShowMenu] = useState<string | null>(null)

  const toggleExpanded = (packageId: string) => {
    setExpandedPackages(prev => {
      const newSet = new Set(prev)
      if (newSet.has(packageId)) {
        newSet.delete(packageId)
      } else {
        newSet.add(packageId)
      }
      return newSet
    })
  }

  const toggleMenu = (packageId: string) => {
    setShowMenu(showMenu === packageId ? null : packageId)
  }

  const sortedPackages = [...packages].sort((a, b) => {
    // Sort by type first, then by price
    const typeDiff = packageTypeOrder[a.type] - packageTypeOrder[b.type]
    if (typeDiff !== 0) return typeDiff
    return a.price - b.price
  })

  if (packages.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <PackageIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            No Packages Found
          </h3>
          <p className="text-neutral-600">
            {packages.length === 0 ? 'Create your first package to get started.' : 'No packages match your filters.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {sortedPackages.map((pkg) => {
        const isExpanded = expandedPackages.has(pkg.id)
        const isMenuOpen = showMenu === pkg.id

        return (
          <Card key={pkg.id} className={`${!pkg.isActive ? 'opacity-75' : ''}`}>
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-neutral-900">{pkg.name}</h3>
                    {pkg.isPopular && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                        <Star className="h-3 w-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                    <Badge className={packageTypeColors[pkg.type]}>
                      {pkg.type}
                    </Badge>
                    {!pkg.isActive && (
                      <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  {pkg.description && (
                    <p className="text-neutral-600 text-sm mb-3">{pkg.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary-600">
                      RM {pkg.price.toLocaleString()}
                    </div>
                    {pkg.maxDistance && (
                      <div className="text-xs text-neutral-500">
                        Within {pkg.maxDistance}km
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleMenu(pkg.id)}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                    {isMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 z-10">
                        <button
                          onClick={() => { onEdit(pkg); toggleMenu(pkg.id) }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 flex items-center space-x-2"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => { onClone(pkg); toggleMenu(pkg.id) }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 flex items-center space-x-2"
                        >
                          <Copy className="h-4 w-4" />
                          <span>Clone</span>
                        </button>
                        <button
                          onClick={() => { onDelete(pkg); toggleMenu(pkg.id) }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center space-x-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="flex items-center space-x-6 text-sm text-neutral-600 mb-4">
                {pkg.serviceAreas.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4" />
                    <span>{pkg.serviceAreas.join(', ')}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1">
                  <PackageIcon className="h-4 w-4" />
                  <span>{pkg.includes.length} included</span>
                </div>
                {pkg.excludes.length > 0 && (
                  <div className="flex items-center space-x-1">
                    <span>{pkg.excludes.length} excluded</span>
                  </div>
                )}
              </div>

              {/* Expand/Collapse Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleExpanded(pkg.id)}
                className="mb-4"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Show Details
                  </>
                )}
              </Button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t pt-4 space-y-4">
                  {/* Included Services */}
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Included Services:</h4>
                    <div className="bg-green-50 rounded-lg p-3">
                      <ul className="space-y-1">
                        {pkg.includes.map((item, index) => (
                          <li key={index} className="text-sm text-green-800 flex items-start">
                            <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Excluded Services */}
                  {pkg.excludes.length > 0 && (
                    <div>
                      <h4 className="font-medium text-neutral-900 mb-2">Excluded Services:</h4>
                      <div className="bg-red-50 rounded-lg p-3">
                        <ul className="space-y-1">
                          {pkg.excludes.map((item, index) => (
                            <li key={index} className="text-sm text-red-800 flex items-start">
                              <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Package Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-500">Sort Order:</span>
                      <span className="ml-2 font-medium">{pkg.sortOrder}</span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Status:</span>
                      <span className="ml-2 font-medium">{pkg.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Created:</span>
                      <span className="ml-2 font-medium">
                        {new Date(pkg.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-500">Updated:</span>
                      <span className="ml-2 font-medium">
                        {new Date(pkg.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(pkg)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onClone(pkg)}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Clone
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(pkg)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}